import "./navbar.scss";
import {
  Avatar,
  Badge,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  styled,
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Logout,
  MenuBook,
  Home,
  LibraryBooks,
  PendingActions,
  AddCircleOutline,
  Menu as MenuIcon,
  Close,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useUserAuth } from "../../context/AuthContext";
import { useUserData } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    borderRadius: '4px',
  },
}));

const Navbar = () => {
  const { logOut } = useUserAuth();
  const { userData } = useUserData();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [nameInitials, setNameInitials] = useState("");
  
  const open = Boolean(anchorEl);
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClickLogout = async () => {
    await logOut();
    navigate("/login");
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileNavClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  useEffect(() => {
    if (userData.fullName) {
      const initials = userData.fullName
        .split(" ")
        .map((word) => word.charAt(0))
        .slice(0, 2)
        .join("");
      setNameInitials(initials);
    }
  }, [userData]);

  const navigationItems = [
    { label: 'HOME', icon: <Home />, path: '/' },
    { label: 'LIBRARY', icon: <LibraryBooks />, path: '/library' },
    { label: 'PENDING BOOKS', icon: <PendingActions />, path: '/pending-books' },
    { label: 'ADD BOOK', icon: <AddCircleOutline />, path: '/add-book' },
  ];

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src="/image.png" alt="Logo" style={{ height: 32, marginRight: 8 }} />
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <Close />
        </IconButton>
      </Box>
      
      <List sx={{ pt: 2 }}>
        {navigationItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton 
              onClick={() => handleMobileNavClick(item.path)}
              sx={{
                py: 1.5,
                px: 3,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                }
              }}
            >
             
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: 500,
                  color: '#333'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />
      
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => handleMobileNavClick('/account')}
            sx={{
              py: 1.5,
              px: 3,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: '14px',
                  backgroundColor: '#1976d2',
                }}
              >
                {nameInitials.toUpperCase()}
              </Avatar>
            </ListItemIcon>
            <ListItemText 
              primary="My Account"
              secondary={userData.fullName}
              primaryTypographyProps={{
                fontWeight: 500,
                color: '#333'
              }}
              secondaryTypographyProps={{
                fontSize: '0.875rem',
                color: '#666'
              }}
            />
          </ListItemButton>
        </ListItem>
        
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleClickLogout}
            sx={{
              py: 1.5,
              px: 3,
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.08)',
              }
            }}
          >
            <ListItemIcon sx={{ color: '#f44336', minWidth: 40 }}>
              <Logout />
            </ListItemIcon>
            <ListItemText 
              primary="Logout"
              primaryTypographyProps={{
                fontWeight: 500,
                color: '#f44336'
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" color="default" elevation={1} sx={{ backgroundColor: 'white', zIndex: 1300 }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: '64px' }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src="/image.png" 
                alt="Logo" 
                style={{ 
                  height: 30, 
                  marginRight: 8,
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/')}
              />
            </Box>
            
            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {navigationItems.map((item) => (
                  <NavButton 
                    key={item.label}
                 
                    onClick={() => navigate(item.path)}
                    sx={{ 
                      display: { xs: 'none', md: 'flex' },
                      minWidth: 'auto',
                      px: 2,
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      fontSize: '0.9rem',
                      fontWeight: 450,
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.06)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {item.label}
                  </NavButton>
                ))}
                
                <IconButton 
                  onClick={handleClick} 
                  size="small" 
                  sx={{ 
                    ml: 2,
                    p: 0.5,
                    border: '2px solid transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      border: '2px solid rgba(25, 118, 210, 0.2)'
                        }
                      }}
                    >
                  <StyledBadge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    variant="dot"
                  >
                    <Avatar 
                      sx={{ 
                        bgcolor: 'rgba(25, 118, 210, 0.8)',
                        width: 38,
                        height: 38,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        fontSize: '0.95rem',
                        fontWeight: 600
                      }}
                    >
                      {nameInitials.toUpperCase()}
                    </Avatar>
                  </StyledBadge>
                </IconButton>
              </Box>
            )}

            {/* Mobile Navigation */}
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={handleClick} size="small" sx={{ mr: 1 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: '#1976d2',
                      width: 36,
                      height: 36,
                      fontSize: '0.875rem'
                    }}
                  >
                    {nameInitials.toUpperCase()}
                  </Avatar>
                </IconButton>
                
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                  sx={{ color: '#1976d2' }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            backgroundColor: '#fafafa'
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop User Menu */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              borderRadius: 2,
              minWidth: 180,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => { navigate("/account"); }} sx={{ py: 1.5, px: 2 }}>
          <Avatar
            sx={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              fontSize: "14px",
              backgroundColor: "#1976d2",
              mr: 1.5
            }}
          >
            {nameInitials.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              My Account
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {userData.fullName}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleClickLogout} sx={{ py: 1.5, px: 2, color: '#f44336' }}>
          <ListItemIcon sx={{ color: '#f44336' }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default Navbar;
